import React, { useState, useEffect, ReactNode, useRef } from 'react';
import { CardWithHeader } from './CardWithHeader';
import { cn } from '../../lib/utils';
import { darkGreenColor, extraSmallTextSize, mediumText, semiBoldText, smallTextSize, tertiaryText, textGreyInfo, regularText, quaternaryText } from '../../styling/stylingHelper';
import { Triangle, ChevronDown, ArrowUp, ArrowDown } from 'lucide-react';
import { ColumnType, renderByType } from './TableValueComponent';
import { useTranslation } from 'react-i18next';
import { ColumnFilterDropdown } from './ColumnFilterDropdown';
import { useAppUserDefaultStore, loadTableItemsPerPageSetting, saveTableItemsPerPageSetting } from '../../stores/appUserDefaultStore';
import { usePracticeScope } from '../onboarding/practice/practiceScope';
import { useAppSettingsStore } from '../../stores/appSettingsStore';

/**
 * A single-select section rendered inside a column's filter dropdown, below the normal
 * multi-select list. Used for choices that are mutually exclusive (pick ONE unit dimension)
 * and for a per-column sort control on columns where the header chevrons don't fit.
 * Absent on every existing table, so their dropdowns render unchanged.
 */
export interface ColumnFilterChoiceConfig {
  presentation?: 'tabs' | 'dropdown';
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
}

export interface ColumnFilterConfig {
  selectedValues?: string[];
  onValuesChange?: (values: string[]) => void;
  availableOptions?: string[] | { id: string; name: string }[];
  valueType?: 'default' | 'category' | 'subcategory' | 'blueQuantity';
  filterLabel?: string;
  maxHeight?: string;
  // Subcategory filter options (shown below main filter)
  subCategorySelectedValues?: string[];
  subCategoryOnValuesChange?: (values: string[]) => void;
  subCategoryAvailableOptions?: string[];
  subCategoryFilterLabel?: string;
  // Dropdown alignment: 'center' (default) or 'left'
  align?: 'center' | 'left';
  // Optional single-select section shown under the multi-select list.
  choice?: ColumnFilterChoiceConfig;
  // Optional sort control for THIS column, for columns that can't carry header chevrons.
  sort?: ColumnFilterChoiceConfig;
}

// Opt-in visual tweaks used by the redesigned inventory pages. Absent options
// (or absent fields) leave the app-wide baseline look byte-exact.
export interface TableComponentOptions {
  // Drop the per-row bottom borders (borderless look).
  borderless?: boolean;
  // Left-align every cell (default keeps non-first columns centered).
  leftAlignCells?: boolean;
  // Never pad blank filler rows under pagination.
  noBlankPadding?: boolean;
  // Extra classes appended to the table header row.
  headerClassName?: string;
}

type ColumnFor<T> = {
  [K in keyof T]: {
    key: K;
    // Plain string in almost every case; ReactNode is used when a column header
    // needs to embed an InfoHelper icon next to the text.
    label: ReactNode;
    columnType?: ColumnType;
    render?: (value: T[K], row: T) => ReactNode;
    filter?: ColumnFilterConfig;
    sortable?: boolean;
    /** Also let the label and header padding trigger the sort. */
    sortOnHeaderClick?: boolean;
    sortKey?: string;
    /** Optional stable anchor on the column header. */
    onboardingId?: string;
    /** Anchor shared by the header and every cell in this column. */
    onboardingColumnId?: string;
  };
}[keyof T];

export interface TableComponentProps<T> {
  title: string;
  data: T[];
  columns: ColumnFor<T>[];
  firstColumn?: 'wide' | 'narrow' | 'narrow-always';
  itemsPerPage?: number;
  blankLines: boolean;
  className?: string;
  seeAllText?: string;
  onSeeAllClick?: () => void;
  onRowClick?: (row: T) => void;
  // When defined, gates whether `onRowClick` fires and whether the row gets the
  // cursor-pointer + hover styling. Lets callers make only a subset of rows
  // clickable (e.g. items-view rows that actually have an add-ons breakdown).
  isRowClickable?: (row: T) => boolean;
  onCategoryClick?: (productName: string, currentCategory?: string) => void;
  onNameClick?: (row: T) => void;
  onQuantityClick?: (row: T) => void;
  onPriceClick?: (row: T) => void;
  searchQuery?: string;
  onSearchChange?: (value: string) => void;
  searchPlaceholder?: string;
  seeAllOnboardingId?: string;
  searchOnboardingId?: string;
  rowOnboardingId?: (row: T) => string | undefined;
  alignOtherColumnsRight?: boolean;
  onExport?: () => void;
  exportButtonText?: string;
  initialPage?: number;
  onPageChange?: (page: number) => void;
  usePagination?: boolean;
  headerActions?: React.ReactNode;
  headerActionsRight?: React.ReactNode;
  /** Optional full-width row between the card header and the table (see CardWithHeader). */
  subHeader?: React.ReactNode;
  sortColumn?: string | null;
  sortDirection?: 'asc' | 'desc' | null;
  onSort?: (column: string) => void;
  /**
   * Filters the page owns outside the table (its TopSection panel). Only its zero/non-zero
   * state is used: it decides whether an empty table reads "no data in period" or "no data
   * matching filters", so page-level filtering isn't mistaken for an empty period.
   */
  activeFilterCount?: number;
  /**
   * Replaces the built-in "Clear filters" behaviour in the empty state. Pass this when the
   * page owns filters beyond the table's own columns and search box — it then clears
   * everything, and the built-in per-column clearing is skipped.
   */
  onClearFilters?: () => void;
  // Optional inline-expanded panel for a row. When this function returns non-null
  // for a row, an extra <tr> is rendered directly under it with a full-width
  // <td colSpan={columns.length}> holding the returned ReactNode. Expansion state
  // (which row is open) is owned by the caller.
  renderExpandedRow?: (row: T) => ReactNode | null;
  // Additive, opt-in styling flags — see TableComponentOptions.
  options?: TableComponentOptions;
}

// Helper to select at most 3 page numbers, always showing current, one before and one after if possible.
function getVisiblePageNumbers(currentPage: number, totalPages: number) {
  if (totalPages <= 3) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }
  if (currentPage === 1) {
    return [1, 2, 3];
  }
  if (currentPage === totalPages) {
    return [totalPages - 2, totalPages - 1, totalPages].filter(n => n > 0);
  }
  return [currentPage - 1, currentPage, currentPage + 1];
}

export const TableComponent = <T extends {}>({
  title,
  data,
  columns,
  firstColumn = 'wide',
  itemsPerPage: itemsPerPageProp,
  blankLines = false,
  className,
  seeAllText,
  onSeeAllClick,
  onRowClick,
  isRowClickable,
  onCategoryClick,
  onNameClick,
  onQuantityClick,
  onPriceClick,
  searchQuery,
  onSearchChange,
  searchPlaceholder,
  seeAllOnboardingId,
  searchOnboardingId,
  rowOnboardingId,
  alignOtherColumnsRight = false,
  onExport,
  exportButtonText,
  initialPage,
  onPageChange,
  usePagination = false,
  headerActions,
  headerActionsRight,
  subHeader,
  sortColumn,
  sortDirection,
  onSort,
  activeFilterCount,
  onClearFilters,
  renderExpandedRow,
  options,
}: TableComponentProps<T>) => {
  const { t } = useTranslation();
  const darkMode = useAppSettingsStore((s) => s.darkMode);
  const {
    borderless = false,
    leftAlignCells = false,
    noBlankPadding = false,
    headerClassName,
  } = options ?? {};
  const [currentPage, setCurrentPage] = useState(initialPage || 1);
  const [isItemsPerPageDropdownOpen, setIsItemsPerPageDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Get items per page from user defaults store
  const inPractice = usePracticeScope();
  const [practicePageSize, setPracticePageSize] = useState(100);
  const itemsPerPageFromStore = useAppUserDefaultStore(s => s.tableItemsPerPage);

  // Determine items per page:
  // - If itemsPerPageProp is provided explicitly, use it (backward compatibility)
  // - If usePagination is true, use store value (new pagination behavior)
  // - Otherwise default to 5 (old non-paginated behavior)
  const itemsPerPage = itemsPerPageProp || (usePagination ? (inPractice ? practicePageSize : itemsPerPageFromStore) : 5);

  // Load items per page setting on component mount (only if using pagination)
  useEffect(() => {
    if (usePagination && !inPractice) {
      loadTableItemsPerPageSetting();
    }
  }, [usePagination, inPractice]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsItemsPerPageDropdownOpen(false);
      }
    };

    if (isItemsPerPageDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isItemsPerPageDropdownOpen]);


  const hasMounted = useRef(false);

  // keep: sync from prop
  useEffect(() => {
    if (typeof initialPage === 'number') setCurrentPage(initialPage);
  }, [initialPage]);

  // change: skip first run
  useEffect(() => {
    if (!hasMounted.current) {
      hasMounted.current = true;
      return;
    }
    setCurrentPage(1);
    onPageChange?.(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemsPerPage, searchQuery]);

  const totalPages = Math.ceil(data.length / itemsPerPage);

  // Reset to page 1 if current page is beyond available pages (e.g., after filtering)
  // But preserve page if initialPage is set and valid (for navigation from detail pages)
  useEffect(() => {
    if (totalPages === 0) {
      // No data, reset to page 1
      if (currentPage !== 1) {
        setCurrentPage(1);
        onPageChange?.(1);
      }
    } else if (currentPage > totalPages) {
      // Current page is beyond available pages
      // If initialPage is provided and valid, use it (preserves navigation from detail pages)
      // Otherwise, reset to page 1
      const targetPage = (typeof initialPage === 'number' && initialPage <= totalPages && initialPage >= 1) 
        ? initialPage 
        : 1;
      
      if (currentPage !== targetPage) {
        setCurrentPage(targetPage);
        onPageChange?.(targetPage);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.length, totalPages, initialPage]);
  const revealedSearch = useRef<string | null>(null);
  // A guided search may match many similar products. Reveal its exact result even
  // when the current sort puts it beyond the first page.
  useEffect(() => {
    const index = searchQuery && rowOnboardingId ? data.findIndex(row => Boolean(rowOnboardingId(row))) : -1;
    if (index < 0) { revealedSearch.current = null; return; }
    const key = `${searchQuery}|${index}|${itemsPerPage}`;
    if (revealedSearch.current === key) return;
    revealedSearch.current = key;
    const page = Math.floor(index / itemsPerPage) + 1;
    setCurrentPage(page);
    onPageChange?.(page);
  }, [searchQuery, rowOnboardingId, data, itemsPerPage, currentPage, onPageChange]);
  const startIdx = (currentPage - 1) * itemsPerPage;
  const paginatedData = data.slice(startIdx, startIdx + itemsPerPage);

  // Always update page if it's a different (and valid) page. Ignore requests outside valid range, but allow click.
  const handlePageChange = (page: number) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
    onPageChange?.(page);
  };

  // Handler for changing items per page
  const handleItemsPerPageChange = async (newItemsPerPage: number) => {
    try {
      if (inPractice) setPracticePageSize(newItemsPerPage);
      else await saveTableItemsPerPageSetting(newItemsPerPage);
      setIsItemsPerPageDropdownOpen(false);
    } catch (error) {
      console.error('Failed to save items per page setting:', error);
    }
  };

  // Calculate showing x-y of z items
  const totalItems = data.length;
  const showingFrom = totalItems === 0 ? 0 : startIdx + 1;
  const showingTo = totalItems === 0 ? 0 : Math.min(startIdx + itemsPerPage, totalItems);

  // Always max 3 page buttons, always current, and always try to show prev/next
  const visiblePages = getVisiblePageNumbers(currentPage, totalPages);

  // Dynamic colgroup for shared width on non-first columns (fr units)
  const colgroup = (
    <colgroup>
      <col style={{ width: '220px', minWidth: '120px', maxWidth: '300px' }} />
      {columns.slice(1).map((_, i) => (
        // So we distribute the rest equally
        <col key={i} style={{ width: `${100 / (columns.length - 1)}%` }} />
      ))}
    </colgroup>
  );

  // A table whose columns carry filters keeps its header row even with zero rows: otherwise a
  // filter that matches nothing takes its own dropdown off screen with it, and the only way
  // back is a page reload. Tables without column filters keep the plain empty state.
  const hasColumnFilters = columns.some(col => !!col.filter);

  /**
   * Whether anything the user picked could be hiding rows, which decides between "no data in
   * period" and "no data matching filters". A `sort` choice is deliberately excluded — sorting
   * reorders rows, it can never remove them. `activeFilterCount` lets a page report filters
   * that live outside the table (its TopSection filter panel).
   */
  const activeFilterTotal =
    (activeFilterCount || 0) +
    (searchQuery ? 1 : 0) +
    columns.reduce((count, col) => {
      const columnFilter = col.filter;
      if (!columnFilter) return count;
      const choice = columnFilter.choice;
      const choiceActive = choice && choice.value !== choice.options[0]?.value ? 1 : 0;
      return (
        count +
        (columnFilter.selectedValues?.length || 0) +
        (columnFilter.subCategorySelectedValues?.length || 0) +
        choiceActive
      );
    }, 0);
  const filtersMayHideRows = activeFilterTotal > 0;

  /**
   * Clears everything this table owns — its column filters and its search box. A page that
   * also owns filters outside the table passes `onClearFilters` and takes over entirely.
   */
  const clearAllFilters = () => {
    if (onClearFilters) {
      onClearFilters();
      return;
    }
    columns.forEach(col => {
      col.filter?.onValuesChange?.([]);
      col.filter?.subCategoryOnValuesChange?.([]);
      const choice = col.filter?.choice;
      if (choice?.options[0]) choice.onChange(choice.options[0].value);
    });
    onSearchChange?.('');
  };

  const emptyState = (
    <div className="flex flex-col items-center justify-center gap-2 w-full">
      <p className={cn(smallTextSize, regularText, quaternaryText, "text-center w-full")}>
        {filtersMayHideRows ? t('common.noDataMatchingFilters') : t('common.noDataInPeriod')}
      </p>
      {filtersMayHideRows && (
        <button
          type="button"
          onClick={clearAllFilters}
          className={cn(smallTextSize, mediumText, "text-darkGreenColor dark:text-darkGreenColor-dark hover:underline")}
        >
          {t('common.clearFilters')}
        </button>
      )}
    </div>
  );

  // Keep short-page padding bounded when the user selects a large page size.
  // But only if blankLines is true OR if using pagination
  var numBlankRows = Math.min(10, itemsPerPage - paginatedData.length);
  // No data means no filler either — the empty-state row below stands in for the whole body.
  if (data.length === 0) {
    numBlankRows = 0;
  }
  if (!blankLines && (totalPages === 1 || !usePagination)) {
    numBlankRows = 0;
  }
  // Opt-in: never render blank filler rows, even under pagination.
  if (noBlankPadding) {
    numBlankRows = 0;
  }

  const blankRows = Array.from({ length: numBlankRows > 0 ? numBlankRows : 0 });

  var firstColumnWidthClass = "text-left min-w-[200px] min-[1250px]:min-w-[300px]"
  if (firstColumn === 'narrow') {
    firstColumnWidthClass = "text-left min-w-[100px] min-[1250px]:min-w-[300px]"
  } else if (firstColumn === 'narrow-always') {
    firstColumnWidthClass = "text-left min-w-[100px] min-[1250px]:min-w-[150px]"
  }

  const shouldShowBorder = itemsPerPage > 5;
  // Opt-in borderless look suppresses the per-row bottom borders.
  const rowBorderActive = shouldShowBorder && !borderless;

  return (
    <CardWithHeader
      title={title}
      seeAllText={seeAllText}
      onSeeAllClick={onSeeAllClick}
      className={cn(className)}
      searchQuery={searchQuery}
      onSearchChange={onSearchChange}
      searchPlaceholder={searchPlaceholder}
      seeAllOnboardingId={seeAllOnboardingId}
      searchOnboardingId={searchOnboardingId}
      onExport={onExport}
      exportButtonText={exportButtonText}
      headerActions={headerActions}
      headerActionsRight={headerActionsRight}
      subHeader={subHeader}
    >
      {data.length === 0 && !hasColumnFilters ? (
        <div className="flex items-center justify-center flex-1 min-h-0 w-full py-8">
          {emptyState}
        </div>
      ) : (
        <div className="w-full">
          <div className="w-[100%]">
            <div className="overflow-x-auto">
              <table className="">
                {colgroup}
                <thead>
                {/* Header row with white background and full-width grey top/bottom border */}
                <tr
                  className={cn(
                    "bg-white dark:bg-background-dark",
                    "border-t border-b",
                    "border-grey-200 dark:border-borderPrimary-dark",
                    headerClassName
                  )}
                  style={{}}
                >
                  {columns.map((col, i) => {
                    const isCategoryColumn = col.columnType === 'category';
                    return (
                      <th
                        key={String(col.key)}
                        data-onboarding={[col.onboardingId, col.onboardingColumnId].filter(Boolean).join(' ') || undefined}
                        // Also the only readable sort signal for pages that keep sort in
                        // component state, which is what the guided tour waits on.
                        aria-sort={
                          !col.sortable
                            ? undefined
                            : sortColumn === col.sortKey && sortDirection === 'asc'
                              ? 'ascending'
                              : sortColumn === col.sortKey && sortDirection === 'desc'
                                ? 'descending'
                                : 'none'
                        }
                        className={cn(
                          extraSmallTextSize,
                          tertiaryText,
                          mediumText,
                          "px-3 py-2 whitespace-nowrap",
                          col.sortOnHeaderClick && "cursor-pointer",
                          i === 0
                            ? firstColumnWidthClass
                            : alignOtherColumnsRight ? "text-right w-[80px]" : leftAlignCells ? "text-left w-[80px]" : "text-center w-[80px]"
                        )}
                        onClick={col.sortOnHeaderClick && col.sortKey && onSort ? () => onSort(col.sortKey!) : undefined}
                        style={isCategoryColumn ? { maxWidth: '120px', width: '120px' } : undefined}
                      >
                        <div className={i === 0 ? undefined : alignOtherColumnsRight ? "flex justify-end items-center w-full" : leftAlignCells ? "flex justify-start items-center w-full" : "flex justify-center items-center w-full"}>
                          {col.label}
                          {col.sortable && col.sortKey && onSort && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                onSort(col.sortKey!);
                              }}
                              className="ml-1.5 flex flex-row items-center justify-center gap-0 hover:opacity-70 transition-opacity"
                              aria-label={`Sort by ${col.sortKey}`}
                            >
                              <ArrowUp 
                                className={cn(
                                  "w-3 h-3",
                                  sortColumn === col.sortKey && sortDirection === 'asc' 
                                    ? "text-[#0E8965] dark:text-darkGreenColor-dark" 
                                    : "text-[#9CA3AF] dark:text-textTertiary-dark"
                                )} 
                              />
                              <ArrowDown 
                                className={cn(
                                  "w-3 h-3",
                                  sortColumn === col.sortKey && sortDirection === 'desc' 
                                    ? "text-[#0E8965] dark:text-darkGreenColor-dark" 
                                    : "text-[#9CA3AF] dark:text-textTertiary-dark"
                                )} 
                              />
                            </button>
                          )}
                          {col.filter && (
                            <ColumnFilterDropdown
                              onboardingId={col.onboardingId}
                              selectedValues={col.filter.selectedValues}
                              onValuesChange={col.filter.onValuesChange}
                              availableOptions={col.filter.availableOptions}
                              valueType={col.filter.valueType}
                              filterLabel={col.filter.filterLabel}
                              maxHeight={col.filter.maxHeight}
                              subCategorySelectedValues={col.filter.subCategorySelectedValues}
                              subCategoryOnValuesChange={col.filter.subCategoryOnValuesChange}
                              subCategoryAvailableOptions={col.filter.subCategoryAvailableOptions}
                              subCategoryFilterLabel={col.filter.subCategoryFilterLabel}
                              align={col.filter.align}
                              choice={col.filter.choice}
                              sort={col.filter.sort}
                            />
                          )}
                        </div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {/* Header stays above this so an over-restrictive filter is still undoable. */}
                {data.length === 0 && (
                  <tr>
                    <td colSpan={columns.length} className="py-8">
                      {emptyState}
                    </td>
                  </tr>
                )}
                {paginatedData.map((row, idx) => {
                  const expandedContent = renderExpandedRow?.(row);
                  // Per-row clickability gate: if `isRowClickable` is provided,
                  // it decides; otherwise fall back to "clickable iff onRowClick exists".
                  const rowClickable = !!onRowClick && (isRowClickable ? isRowClickable(row) : true);
                  return (
                  <React.Fragment key={idx}>
                  <tr
                    data-onboarding={rowOnboardingId?.(row)}
                    className={cn(
                      "py-2",
                      rowClickable && "cursor-pointer hover:bg-neutral-50 dark:hover:bg-backgroundDarker-dark transition-colors",
                      // Hide the bottom border on the main row when an expanded
                      // panel is being rendered just below — the expanded <tr>
                      // owns the border so the row + panel read as one block.
                      rowBorderActive && !expandedContent && "border-b",
                      rowBorderActive && !expandedContent && "border-grey-200 dark:border-borderPrimary-dark"
                    )}
                    onClick={() => rowClickable && onRowClick?.(row)}
                  >
                    {columns.map((col, i) => {
                      const value = row[col.key];
                      const content: ReactNode = col.render
                        ? col.render(value, row)
                        : col.columnType
                        ? renderByType(value, col.columnType, row, onCategoryClick, onNameClick, onQuantityClick, onPriceClick, darkMode)
                        : String(value);

                      const titleValue = typeof value === 'string' ? value : undefined;

                      // Special handling for category column
                      const isCategoryColumn = col.columnType === 'category';
                      
                      return (
                        <td
                          key={String(col.key)}
                          data-onboarding={col.onboardingColumnId}
                          className={cn(
                            "px-3 py-2",
                            i === 0 ? "text-left w-[250px]" : alignOtherColumnsRight ? "text-right w-[80px]" : leftAlignCells ? "text-left w-[80px]" : "text-center w-[80px]"
                          )}
                          style={
                            i === 0
                              ? {
                                  width: '300px',
                                  minWidth: '120px',
                                  maxWidth: '300px',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }
                              : isCategoryColumn
                              ? {
                                  maxWidth: '120px',
                                  width: '120px',
                                  overflow: 'hidden',
                                }
                              : {
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                }
                          }
                        >
                          {isCategoryColumn ? (
                            <div
                              className="overflow-x-auto"
                              style={{
                                maxWidth: '120px',
                                width: '100%',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {content}
                            </div>
                          ) : (
                            <span
                              style={{
                                display: 'block',
                                width: '100%',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}
                              title={titleValue}
                              className={cn("py-2", i === 0 ? undefined : alignOtherColumnsRight ? "flex justify-end items-center w-full" : leftAlignCells ? "flex justify-start items-center w-full" : "flex justify-center items-center w-full")}
                            >
                              {content}
                            </span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                  {expandedContent && (
                    <tr
                      className={cn(
                        rowBorderActive && "border-b",
                        rowBorderActive && "border-grey-200 dark:border-borderPrimary-dark"
                      )}
                    >
                      <td colSpan={columns.length} className="p-0">
                        {expandedContent}
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                  );
                })}
                {/* Render blank rows if needed to preserve table height */}
                {blankRows.map((_, idx) => (
                  <tr
                    key={`blank-row-${idx}`}
                    className={cn(
                      "py-2",
                      rowBorderActive && "border-b",
                      rowBorderActive && "border-grey-200 dark:border-borderPrimary-dark"
                    )}
                  >
                    {columns.map((col, colIdx) => (
                      <td
                        key={String(col.key)}
                        className={cn(
                          "px-4 py-2",
                          colIdx === 0 ? "text-left" : alignOtherColumnsRight ? "text-right" : leftAlignCells ? "text-left" : "text-center"
                        )}
                        style={
                          colIdx === 0
                            ? {
                                width: '300px',
                                minWidth: '120px',
                                maxWidth: '300px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }
                            : {
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }
                        }
                      >
                        {/* Empty cell for blank/filler row */}
                        <span
                          style={{
                            display: 'block',
                            width: '100%',
                            minHeight: '2.5rem' // Ensure row is same min height as normal row content (py-2 == 0.5rem top/bottom padding + content)
                          }}
                          className={cn("py-2", colIdx === 0 ? undefined : alignOtherColumnsRight ? "flex justify-end items-center w-full" : leftAlignCells ? "flex justify-start items-center w-full" : "flex justify-center items-center w-full")}
                          aria-hidden="true"
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Showing x-y of z items and right-aligned pagination */}
        <div className="mt-2 flex flex-row items-center justify-between">
          {/* Show the "Showing x-y of z" text with clickable dropdown only if using pagination */}
          {usePagination ? (
            <div className={cn(smallTextSize, textGreyInfo, mediumText, "flex items-center gap-1")}>
              <span>{t('common.showing')} {showingFrom}-</span>
              <div className="relative inline-block" ref={dropdownRef}>
                <button
                  onClick={() => setIsItemsPerPageDropdownOpen(!isItemsPerPageDropdownOpen)}
                  className={cn(
                    "inline-flex items-center gap-0.5 hover:text-darkGreenColor dark:hover:text-darkGreenColor-dark transition-colors cursor-pointer",
                    "focus:outline-none focus:text-darkGreenColor dark:focus:text-darkGreenColor-dark"
                  )}
                  type="button"
                >
                  <span className="font-semibold">{showingTo}</span>
                  <ChevronDown className="w-3 h-3" />
                </button>
                {isItemsPerPageDropdownOpen && (
                  <div className="absolute left-0 bottom-full mb-1 bg-white dark:bg-background-dark border border-grey-200 dark:border-borderPrimary-dark rounded-lg shadow-lg py-1 z-50 min-w-[80px]">
                    {[10, 15, 50, 100].map((option) => (
                      <button
                        key={option}
                        onClick={() => handleItemsPerPageChange(option)}
                        className={cn(
                          "w-full px-4 py-2 text-left hover:bg-neutral-50 dark:hover:bg-backgroundDarker-dark transition-colors",
                          smallTextSize,
                          option === itemsPerPage ? cn(semiBoldText, "text-darkGreenColor dark:text-darkGreenColor-dark") : regularText
                        )}
                        type="button"
                      >
                        {option}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <span>{t('common.of')} {totalItems}</span>
            </div>
          ) : (
            <div className={cn(smallTextSize, textGreyInfo, mediumText)}>
              {`${t('common.showing')} ${showingFrom}-${showingTo} ${t('common.of')} ${totalItems}`}
            </div>
          )}
          {totalPages > 1 && (
            <div className="flex items-center space-x-2">
              {/* Previous Button */}
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                // Always enabled, even when on first page
                className={cn(
                  "w-8 h-8 rounded-lg flex items-center justify-center",
                  smallTextSize,
                  semiBoldText,
                  "border border-darkGreenColor dark:border-darkGreenColor-dark",
                )}
                aria-label={t('common.previousPage')}
                tabIndex={0}
                type="button"
              >
                <Triangle
                  fill={darkGreenColor}
                  color={darkGreenColor}
                  strokeWidth={0}
                  className="rotate-[-90deg] w-1.5 h-1.5"
                />
              </button>

              {/* Max 3 page number buttons */}
              <div
                className="flex space-x-1"
              >
                {visiblePages.map((pageNum) => (
                  <button
                    key={pageNum}
                    onClick={() => handlePageChange(pageNum)}
                    className={cn(
                      // Always exactly 8x8 tailwind units
                      "w-8 h-8 rounded-lg flex items-center justify-center",
                      smallTextSize,
                      semiBoldText,
                      pageNum === currentPage
                        ? "bg-darkGreenColor dark:bg-darkGreenColor-dark text-white dark:text-white border border-darkGreenColor dark:border-darkGreenColor-dark"
                        : "bg-white dark:bg-background-dark text-textGreyInfo border border-grey-200 dark:border-borderPrimary-dark hover:bg-neutral-50 dark:hover:bg-backgroundDarker-dark"
                    )}
                    aria-current={pageNum === currentPage ? 'page' : undefined}
                  >
                    {pageNum}
                  </button>
                ))}
              </div>

              {/* Next Button */}
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                // Always enabled, even when on last page
                className={cn(
                  "w-8 h-8 rounded-lg flex items-center justify-center",
                  smallTextSize,
                  semiBoldText,
                  "border border-darkGreenColor dark:border-darkGreenColor-dark",
                )}
                aria-label={t('common.nextPage')}
                tabIndex={0}
                type="button"
              >
                <Triangle
                  fill={darkGreenColor}
                  color={darkGreenColor}
                  strokeWidth={0}
                  className="rotate-90 w-1.5 h-1.5"
                />
              </button>
            </div>
          )}
        </div>
        </div>
      )}
    </CardWithHeader>
  );
};
